import { Check, Clock3, Loader2, UserCheck, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";

import { Button, cn } from "@link/ui";

import { api } from "../lib/api";

type Relationship = "loading" | "none" | "outgoing" | "incoming" | "accepted" | "error";

export function FriendActionButton({
  accountXid,
  displayName,
  compact = false,
  className,
}: {
  accountXid: string;
  displayName: string;
  compact?: boolean;
  className?: string;
}) {
  const [relationship, setRelationship] = useState<Relationship>("loading");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.friends.list().then(({ friends }) => {
      if (cancelled) return;
      const friend = friends.find((item) => item.account_xid === accountXid);
      if (friend === undefined) setRelationship("none");
      else if (friend.status === "accepted") setRelationship("accepted");
      else setRelationship(friend.direction === "incoming" ? "incoming" : "outgoing");
    }).catch(() => { if (!cancelled) setRelationship("none"); });
    return () => { cancelled = true; };
  }, [accountXid]);

  const act = async (): Promise<void> => {
    if (relationship !== "none" && relationship !== "incoming" && relationship !== "error") return;
    const accepting = relationship === "incoming";
    setRelationship("loading");
    setMessage(null);
    try {
      if (accepting) {
        await api.friends.accept(accountXid);
        setRelationship("accepted");
        setMessage(`${displayName} is now your friend.`);
      } else {
        await api.friends.request({ account_xid: accountXid });
        setRelationship("outgoing");
        setMessage(`Friend request sent to ${displayName}.`);
      }
    } catch (reason) {
      setRelationship("error");
      setMessage(reason instanceof Error ? reason.message : "Couldn't update this friend request.");
    }
  };

  const label = relationship === "accepted" ? "Friends" : relationship === "outgoing" ? "Request sent" : relationship === "incoming" ? "Accept request" : relationship === "error" ? "Try again" : relationship === "loading" ? "Checking…" : "Add friend";
  const icon = relationship === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : relationship === "accepted" ? <UserCheck className="h-4 w-4" /> : relationship === "outgoing" ? <Clock3 className="h-4 w-4" /> : relationship === "incoming" ? <Check className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />;
  const disabled = relationship === "loading" || relationship === "accepted" || relationship === "outgoing";

  if (compact) {
    return (
      <button
        type="button"
        title={message ?? label}
        aria-label={`${label} ${displayName}`}
        disabled={disabled}
        onClick={() => void act()}
        className={cn("flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-cloud-700 ring-1 ring-white/50 backdrop-blur transition hover:bg-white disabled:cursor-default disabled:opacity-80", className)}
      >
        {icon}
      </button>
    );
  }

  return (
    <div>
      <Button disabled={disabled} onClick={() => void act()} className={className}>{icon}<span className="ml-2">{label}</span></Button>
      {message !== null ? <p className={cn("mt-1.5 text-xs", relationship === "error" ? "text-red-500" : "text-cloud-500")}>{message}</p> : null}
    </div>
  );
}
